import z from "zod";
import { create } from "zustand/react";

import {
  phase6EvidenceRefSchema,
  phase6SemanticKindValues,
} from "@/integrations/multiresume/ai/optimization-plan-contract";
import { aiEditResultSchema } from "@/integrations/multiresume/ai/section-editing-contract";
import {
  awardItemSchema,
  certificationItemSchema,
  coverLetterItemSchema,
  customSectionSchema,
  educationItemSchema,
  experienceItemSchema,
  interestItemSchema,
  languageItemSchema,
  profileItemSchema,
  projectItemSchema,
  publicationItemSchema,
  referenceItemSchema,
  skillItemSchema,
  summaryItemSchema,
  volunteerItemSchema,
} from "@/schema/resume/data";

const aiEditTargetDialogSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("summary") }),
  z.object({ type: z.literal("experience"), itemId: z.string().min(1) }),
  z.object({ type: z.literal("project"), itemId: z.string().min(1) }),
]);

const dialogTypeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("auth.change-password"), data: z.undefined() }),
  z.object({ type: z.literal("auth.two-factor.enable"), data: z.undefined() }),
  z.object({ type: z.literal("auth.two-factor.disable"), data: z.undefined() }),
  z.object({ type: z.literal("api-key.create"), data: z.undefined() }),
  z.object({ type: z.literal("resume.create"), data: z.undefined() }),
  z.object({
    type: z.literal("resume.target.create"),
    data: z.object({
      sourceMasterResumeId: z.string().optional(),
      sourceMasterName: z.string().optional(),
      createMasterFirst: z.boolean().optional(),
    }),
  }),
  z.object({
    type: z.literal("resume.update"),
    data: z.object({ id: z.string(), name: z.string(), slug: z.string(), tags: z.array(z.string()) }),
  }),
  z.object({
    type: z.literal("resume.target.update"),
    data: z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      tags: z.array(z.string()),
      targetContext: z.object({
        jdText: z.string(),
        jdSummary: z.string().nullable(),
        company: z.string().nullable(),
        roleTitle: z.string().nullable(),
        jobLink: z.string().nullable(),
        supplementalInstructions: z.string().nullable(),
      }),
    }),
  }),
  z.object({
    type: z.literal("resume.master-pdf-update"),
    data: z.object({ masterResumeId: z.string(), masterName: z.string(), masterUpdatedAt: z.date() }),
  }),
  z.object({ type: z.literal("resume.import"), data: z.undefined() }),
  z.object({
    type: z.literal("resume.duplicate"),
    data: z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      tags: z.array(z.string()),
      shouldRedirect: z.boolean().optional(),
    }),
  }),
  z.object({ type: z.literal("resume.template.gallery"), data: z.undefined() }),
  z.object({ type: z.literal("resume.summary.optimize"), data: z.undefined() }),
  z.object({
    type: z.literal("resume.ai-edit"),
    data: z.object({
      target: aiEditTargetDialogSchema,
      requestedOperation: z.enum(["generate", "regenerate"]).optional(),
      guidedReview: z.object({ note: z.string().trim().max(2000) }).optional(),
      pickupReview: z
        .object({
          completeResumeId: z.string().trim().min(1).max(64),
          candidateId: z.string().trim().min(1).max(128),
          sourceTargetResumeId: z.string().trim().min(1).max(64),
          expectedTargetResumeFingerprint: z.string().trim().min(1).max(128),
          expectedMasterResumeFingerprint: z.string().trim().min(1).max(128),
        })
        .optional(),
      recommendation: z
        .object({
          planId: z.string().optional(),
          itemId: z.string(),
          title: z.string(),
          suggestions: z.array(z.string()),
          reasons: z.array(z.string()).optional(),
          proposalType: z.enum(["update_item", "hide_in_target", "restore_item"]).optional(),
          draft: aiEditResultSchema.optional(),
          status: z
            .enum(["pending", "applied", "unresolved", "ignored", "edited", "adjustment_pending", "adjustment_saved"])
            .optional(),
          adjustmentNote: z.string().trim().min(1).max(2000).optional(),
          semanticKind: z.enum(phase6SemanticKindValues).optional(),
          evidenceRefs: z.array(phase6EvidenceRefSchema).optional(),
          jdRefs: z.array(z.string()).optional(),
          requirementRefs: z.array(z.string()).optional(),
          findingRefs: z.array(z.string()).optional(),
          factBoundary: z.string().optional(),
          currentIssue: z.string().optional(),
          recommendedAction: z.string().optional(),
          riskOrGap: z.string().optional(),
          fingerprintInputs: z
            .object({
              sourceFingerprint: z.string().optional(),
              targetContextFingerprint: z.string().optional(),
            })
            .optional(),
        })
        .optional(),
    }),
  }),
  z.object({
    type: z.literal("resume.sections.profiles.create"),
    data: z.object({ item: profileItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.profiles.update"),
    data: z.object({ item: profileItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.experience.create"),
    data: z.object({ item: experienceItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.experience.update"),
    data: z.object({ item: experienceItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.education.create"),
    data: z.object({ item: educationItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.education.update"),
    data: z.object({ item: educationItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.projects.create"),
    data: z.object({ item: projectItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.projects.update"),
    data: z.object({ item: projectItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.skills.create"),
    data: z.object({ item: skillItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.skills.update"),
    data: z.object({ item: skillItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.languages.create"),
    data: z.object({ item: languageItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.languages.update"),
    data: z.object({ item: languageItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.awards.create"),
    data: z.object({ item: awardItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.awards.update"),
    data: z.object({ item: awardItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.certifications.create"),
    data: z.object({ item: certificationItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.certifications.update"),
    data: z.object({ item: certificationItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.publications.create"),
    data: z.object({ item: publicationItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.publications.update"),
    data: z.object({ item: publicationItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.interests.create"),
    data: z.object({ item: interestItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.interests.update"),
    data: z.object({ item: interestItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.volunteer.create"),
    data: z.object({ item: volunteerItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.volunteer.update"),
    data: z.object({ item: volunteerItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.references.create"),
    data: z.object({ item: referenceItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.references.update"),
    data: z.object({ item: referenceItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.summary.create"),
    data: z.object({ item: summaryItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.summary.update"),
    data: z.object({ item: summaryItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("resume.sections.cover-letter.create"),
    data: z.object({ item: coverLetterItemSchema.optional(), customSectionId: z.string().optional() }).optional(),
  }),
  z.object({
    type: z.literal("resume.sections.cover-letter.update"),
    data: z.object({ item: coverLetterItemSchema, customSectionId: z.string().optional() }),
  }),
  z.object({ type: z.literal("resume.sections.custom.create"), data: customSectionSchema.optional() }),
  z.object({ type: z.literal("resume.sections.custom.update"), data: customSectionSchema }),
]);

type DialogSchema = z.infer<typeof dialogTypeSchema>;
type DialogType = DialogSchema["type"];

type DialogData<T extends DialogType> = Extract<DialogSchema, { type: T }>["data"];

type DialogPropsData<T extends DialogType> = DialogData<T> extends undefined ? {} : { data: DialogData<T> };

export type DialogProps<T extends DialogType> = DialogPropsData<T>;

interface DialogStoreState {
  open: boolean;
  activeDialog: DialogSchema | null;
  closeGuard: (() => Promise<boolean>) | null;
}

interface DialogStoreActions {
  onOpenChange: (open: boolean) => void;
  openDialog: <T extends DialogType>(type: T, data: DialogData<T>) => void;
  closeDialog: (force?: boolean) => void;
  setCloseGuard: (guard: (() => Promise<boolean>) | null) => void;
}

type DialogStore = DialogStoreState & DialogStoreActions;

let pendingDialogCleanup: ReturnType<typeof setTimeout> | null = null;

function clearPendingDialogCleanup() {
  if (!pendingDialogCleanup) return;
  clearTimeout(pendingDialogCleanup);
  pendingDialogCleanup = null;
}

function scheduleDialogCleanup(set: (state: Partial<DialogStoreState>) => void) {
  clearPendingDialogCleanup();
  pendingDialogCleanup = setTimeout(() => {
    pendingDialogCleanup = null;
    set({ activeDialog: null });
  }, 300);
}

export const useDialogStore = create<DialogStore>((set, get) => ({
  open: false,
  activeDialog: null,
  closeGuard: null,
  onOpenChange: (open) => {
    if (open) {
      clearPendingDialogCleanup();
      set({ open: true });
      return;
    }

    // Closing: consult the close guard before allowing close
    const guard = get().closeGuard;
    if (guard) {
      void guard().then((allowed) => {
        if (allowed) {
          set({ open: false });
          scheduleDialogCleanup(set);
        }
      });
      return;
    }

    set({ open: false });
    scheduleDialogCleanup(set);
  },
  openDialog: (type, data) => {
    clearPendingDialogCleanup();
    set({
      open: true,
      activeDialog: { type, data } as DialogSchema,
    });
  },
  closeDialog: (force = false) => {
    const guard = force ? null : get().closeGuard;
    if (guard) {
      void guard().then((allowed) => {
        if (allowed) {
          set({ open: false });
          scheduleDialogCleanup(set);
        }
      });
      return;
    }
    set({ open: false });
    scheduleDialogCleanup(set);
  },
  setCloseGuard: (guard) => set({ closeGuard: guard }),
}));
